/**
 * **选中 / 活动节点 / 画布点击** —— 从 Playground.vue 抽出来。
 *
 * ## 这个文件负责什么
 *
 * 三件容易混淆的事在这里分清：
 *
 *   选中（selected）        可以有很多个（框选出来的一批）
 *   活动节点（active）      只有一个，属性面板跟随它
 *   空格（spaceHeld）       按着时把 `panOnDrag` 临时切成 `true`
 *
 * 「选中」的**真相在图引擎里**（`graph/flowStore.ts` 的 `selectedIdsOf`），
 * 这里只是把读写打包；活动节点则是本文件自己维护的 `ref`。
 *
 * ## ⚠️ 抽出来的方式：函数体逐字不动
 *
 * `flowStore` / `activeNodeId` / `nodeViews` / `modelOf` 由 `ctx` 注入 ——
 * 后三个声明得比这里晚，所以一律用**闭包**（见 `SelectionCtx` 的注释）。
 */
import { computed, ref } from 'vue'
import { selectedEdgeIdsOf, selectedIdsOf } from '@/graph/flowStore'
import type { FlowStoreApi } from '@/graph/flowStore'
import type { GraphNodeModel } from '@/types/node'
import type { NodeView } from './useSubgraphLevels'

export interface SelectionCtx {
  flowStore: { value: FlowStoreApi | null }
  /** 活动节点（本文件自己维护，注入进来一起管） */
  activeNodeId: { value: string | null }
  /** 图引擎的节点视图（声明在宿主更靠后的位置，所以用闭包） */
  nodeViews: () => NodeView[]
  /** 按 id 取模型（同上） */
  modelOf: (id: string) => GraphNodeModel | undefined
  /**
   * **把这个节点提到最上面**（写模型的 `z`）。
   *
   * ⚠️ 由宿主注入而不是在这里写：这里不认识"叠放次序"那套，
   * 而且 `z` 的分配规则（`max+1` / 什么时候重排 / 框不抬）是宿主的事。
   */
  raiseNode: (id: string) => void
}

export function useSelection(ctx: SelectionCtx) {
  const { flowStore, activeNodeId, nodeViews, modelOf, raiseNode } = ctx

/* ==========================================================================
   活动节点与选中
   ========================================================================== */

/**
 * 活动节点 —— 多选时属性面板跟随的那一个。
 *
 * 和「选中」是两件事：选中可以有很多个（框选出来的一批），活动只有一个。
 * 所以这里自己维护，而不是复用图引擎的 selected。
 */

/**
 * 空格是否按着 —— 按着时把 panOnDrag 临时切成 `true`。
 *
 * 为什么非这样不可：panOnDrag 传数组时，图引擎的 filter 按**鼠标键编号**
 * 逐个放行，而那个判断**根本不看 panActivationKeyCode**：
 *
 *     if (Array.isArray(panOnDrag) && !panOnDrag.includes(eventButton)) return false
 *
 * 所以在 `[1]` 之下，空格 + 左键（button 0）永远进不来 —— 这就是「空格没反应」。
 * 切成 `true` 才能让空格生效（触控板没有中键，这个通道不能少）。
 */
const spaceHeld = ref(false)

const selectedEdgeIds = computed(() => selectedEdgeIdsOf(flowStore.value))

const selectedIds = computed(() => {
  // 前两级来源在 graph/flowStore.ts 里（有集成测试覆盖）
  const fromStore = selectedIdsOf(flowStore.value)
  if (fromStore.length > 0) return fromStore
  // 最后兜底：万一某个版本把选中态同步回了 props 那份 nodes
  return nodeViews()
    .filter((n) => n.selected)
    .map((n) => n.id)
})

const selectedIdSet = computed(() => new Set(selectedIds.value))

/** 有东西被选中（节点或连线）—— Delete 这类提示才显示 */
const hasSelection = computed(
  () => selectedIds.value.length > 0 || selectedEdgeIds.value.length > 0,
)

const activeModel = computed(() =>
  activeNodeId.value ? modelOf(activeNodeId.value) ?? null : null,
)

function onNodeClick(params: { node: { id: string } }) {
  activeNodeId.value = params.node.id
  /*
   * ★ 点一下 = 提到最上面（用户 2027 要的那条）。**不是**临时抬升 ——
   * 取消选中之后它还在上面（见 `DesignNode.z`）。
   */
  raiseNode(params.node.id)
}

/*
 * 点画布空白 → 取消活动节点。
 *
 * 这里**必须无条件清**，不能再等「选中空了才清」：
 * 图引擎的 onClick 是 `emits.paneClick(event)` 之后才 `removeSelectedElements()`，
 * 也就是说 paneClick 触发的那一刻选中**还没清**。加条件的后果就是
 * 「点两次背景才关得掉面板」。
 *
 * 而 paneClick 不会被节点点击误触发 —— 它的 handler 被 wrapHandler 包着，
 * 要求 `event.target === containerRef`。
 */
function onPaneClick() {
  activeNodeId.value = null
}

  return {
    spaceHeld,
    selectedEdgeIds,
    selectedIds,
    selectedIdSet,
    hasSelection,
    activeModel,
    onNodeClick,
    onPaneClick,
  }
}
